import React, { useState, useRef, useEffect } from "react";
import Modal from "react-modal";
import { FaFilePdf, FaTimes, FaFileVideo, FaPaperclip } from "react-icons/fa";

Modal.setAppElement("#root");

const AttachmentButton = ({ attach, onOpen }) => {
  if (!attach) return null;

  const fileName = attach.basename || attach.name || attach.filename || "Dosya";
  const fileExtension = fileName.split(".").pop()?.toLowerCase() || "";
  const ctype = attach.CTYPE ? attach.CTYPE.toLowerCase() : fileExtension;

  let type = ctype;
  const videoTypes = ['mp4', 'avi', 'mov', 'wmv', 'mkv', 'webm', 'flv'];
  const imageTypes = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'svg', 'webp'];
  if (videoTypes.includes(type)) type = 'video';
  if (imageTypes.includes(type)) type = 'image';

  const iconMap = {
    pdf: <FaFilePdf className="text-slate-100" />,
    video: <FaFileVideo className="text-slate-100" />,
    image: <FaPaperclip className="text-slate-100" />,
    default: <FaPaperclip className="text-slate-100" />,
  };

  const icon = iconMap[type] || iconMap.default;

  return (
    <button
      onClick={() => onOpen(type, attach.URL)}
      className="inline-flex items-center gap-1 rounded bg-white/12 px-1.5 py-0.5 text-[9px] font-medium text-slate-100 transition hover:bg-white/20"
    >
      {icon}
      <span className="max-w-[120px] truncate text-left">{fileName}</span>
    </button>
  );
};

const FormView = ({ formData, flowEvents = [] }) => {
  const containerRef = useRef(null);
  const [modalContent, setModalContent] = useState({ type: "", url: "" });
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  }, [formData]);

  if (!formData || Object.keys(formData).length === 0) {
    return <p className="text-sm italic text-slate-300">Veri bulunamadı.</p>;
  }

  const openModal = (type, url) => {
    setModalContent({ type, url });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalContent({ type: "", url: "" });
  };

  const renderValue = (value) => {
    if (Array.isArray(value)) {
      return (
        <div className="flex flex-wrap gap-1">
          {value.map((item, index) => (
            <AttachmentButton key={`${item?.ATTID || index}`} attach={item} onOpen={openModal} />
          ))}
        </div>
      );
    }

    if (value && typeof value === 'object') {
      return (
        <pre className="max-h-32 overflow-auto rounded-lg bg-black/20 px-2 py-1.5 text-[10px] text-blue-100">
          {JSON.stringify(value, null, 2)}
        </pre>
      );
    }

    return (
      <span className="break-words text-[10px] leading-relaxed text-slate-50">
        {value === null || value === undefined || value === '' ? <span className="text-slate-600">—</span> : String(value)}
      </span>
    );
  };

  const groupBySirano = (entries) => {
    if (!entries || entries.length === 0) return [];
    const hasSirano = entries.some(
      (e) => e && typeof e === 'object' && Object.keys(e).some((k) => k.toUpperCase() === 'SIRANO')
    );
    if (!hasSirano) return [{ sirano: null, fields: entries }];

    const groups = [];
    let current = null;

    entries.forEach((entry) => {
      if (!entry || typeof entry !== 'object') return;
      Object.entries(entry).forEach(([key, value]) => {
        if (key.toUpperCase() === 'SIRANO') {
          if (current) groups.push(current);
          current = { sirano: value, fields: [] };
        } else {
          if (!current) current = { sirano: null, fields: [] };
          current.fields.push({ key, value });
        }
      });
    });
    if (current) groups.push(current);
    return groups;
  };

  return (
    <div ref={containerRef} className="text-slate-100">
      {Object.entries(formData).map(([sectionKey, sectionValue]) => {
        if (!sectionValue) return null;

        const entries = Array.isArray(sectionValue) ? sectionValue : [sectionValue];
        const groups = groupBySirano(entries);

        return (
          <div key={sectionKey}>
            {groups.map((group, gIdx) => {
              if (group.sirano === null && group.fields.length > 0) {
                return (
                  <div key={`${sectionKey}-ungrouped`}>
                    {group.fields.map((entry, idx) =>
                      typeof entry === 'object' && entry.key !== undefined ? (
                        <div key={`${sectionKey}-ug-${idx}`} className="flex items-baseline gap-3 py-1.5">
                          <dt className="w-24 flex-shrink-0 text-[9px] font-semibold uppercase tracking-wider text-slate-400">
                            {entry.key}
                          </dt>
                          <dd className="flex-1 min-w-0">{renderValue(entry.value)}</dd>
                        </div>
                      ) : typeof entry === 'object' ? (
                        <dl key={`${sectionKey}-ug-dl-${idx}`} className="divide-y divide-white/[0.06]">
                          {Object.entries(entry).map(([fieldKey, fieldValue]) => (
                            <div key={fieldKey} className="flex items-baseline gap-3 py-1.5">
                              <dt className="w-24 flex-shrink-0 text-[9px] font-semibold uppercase tracking-wider text-slate-400">
                                {fieldKey}
                              </dt>
                              <dd className="flex-1 min-w-0">{renderValue(fieldValue)}</dd>
                            </div>
                          ))}
                        </dl>
                      ) : (
                        <div key={`${sectionKey}-ug-${idx}`} className="py-1.5">{renderValue(entry)}</div>
                      )
                    )}
                  </div>
                );
              }

              return (
                <div
                  key={`${sectionKey}-g-${gIdx}`}
                  className="mb-1.5 border-b border-white/[0.06] last:border-b-0"
                >
                  <div className="flex items-center gap-2 py-2">
                    <span className="text-[10px] font-bold text-slate-300 bg-slate-700/60 rounded px-2 py-0.5 tabular-nums">
                      #{group.sirano}
                    </span>
                    <div className="flex-1 h-px bg-slate-700/40" />
                  </div>
                  <dl className="divide-y divide-white/[0.04]">
                    {group.fields.map((field, fIdx) => (
                      <div key={`${sectionKey}-g-${gIdx}-f-${fIdx}`} className="flex items-baseline gap-3 py-1.5">
                        <dt className="w-28 flex-shrink-0 text-[9px] font-semibold uppercase tracking-wider text-slate-400/80">
                          {field.key}
                        </dt>
                        <dd className="flex-1 min-w-0">{renderValue(field.value)}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              );
            })}
          </div>
        );
      })}

      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        className="bg-[#101b30] text-slate-100 rounded-2xl shadow-2xl w-full max-w-5xl mx-auto"
        overlayClassName="fixed inset-0 bg-black/70 flex items-center justify-center"
      >
        <header className="flex items-center justify-between border-b border-white/10 px-5 py-3">
          <h2 className="text-sm font-semibold uppercase tracking-[0.28em] text-blue-100/80">
            Ek Önizleme
          </h2>
          <button
            onClick={closeModal}
            className="rounded-full border border-white/15 p-1.5 text-sm text-slate-200 transition hover:border-white/35 hover:bg-white/10"
          >
            <FaTimes />
          </button>
        </header>
        <div className="max-h-[70vh] overflow-auto px-5 py-4">
          {modalContent.type === "video" && (
            <video
              controls
              src={modalContent.url}
              className="h-[420px] w-full rounded-xl bg-black/40 object-contain"
            />
          )}
          {modalContent.type === "pdf" && (
            <iframe
              src={modalContent.url}
              title="PDF Preview"
              className="h-[420px] w-full rounded-xl bg-white"
            />
          )}
          {modalContent.type === "image" && (
            <img
              src={modalContent.url}
              alt="Preview"
              className="max-h-[420px] w-full rounded-xl object-contain"
            />
          )}
          {modalContent.type !== "video" &&
            modalContent.type !== "pdf" &&
            modalContent.type !== "image" && (
              <div className="flex h-[420px] items-center justify-center rounded-xl bg-white/8 text-sm text-slate-200">
                Önizleme desteklenmiyor.
              </div>
            )}
        </div>
      </Modal>
    </div>
  );
};

export default FormView;
